import { useEffect, useState } from 'react';
import { eventLogsColumns } from '../../../tableColumns/hardwareColumns';
import { IHardwareEvent } from '../../../ts/interface/hardware.interface';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import CardView from '../../../components/Redesing/card-view';
import { useParams } from 'react-router-dom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import useHardwareEventLogs from '../../../hooks/hardware/query/useHardwareEventLogs';
import { DEFAULT_ROWS_PER_PAGE, EVENT_LOGS_TYPES } from '../../../constants';
import { SeverityPillColor } from '../../../ts/types/settings';
import useUrlState from '../../../hooks/use-url-state';
import { SearchChip } from '../../../ts/interface/components/table.interface';
import { MovaticFieldGroupDate } from '../../../components/Redesing/movatic-field-group';

const EventLogs = ({
  hardware_id,
  isLoadingHardware,
}: {
  hardware_id: string;
  isLoadingHardware: boolean;
}) => {
  const { type } = useParams<{ type: string; hardwareId: string }>();
  const [exportData, setExportData] = useState<boolean>(false);
  const [events, setEvents] = useState<IHardwareEvent[]>([]);

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    pageNumber: 0,
    search: '',
    after_created_on: '',
    before_created_on: '',
    lastId: null as string | null,
    sortConfig: {
      key: 'created_on',
      direction: 'desc',
    } as ISortConfig,
  });

  const [chips, setChips] = useState<SearchChip[]>([]);

  const [selectedAfterCreatedOn, setSelectedAfterCreatedOn] = useState<Date | null>(null);
  const [selectedbeforeCreatedOn, setSelectedbeforeCreatedOn] = useState<Date | null>(
    null
  );

  const { refetch, isRefetching, isLoading } = useHardwareEventLogs(
    hardware_id,
    {
      limit: filters.lastId ? filters.sizePerPage : filters.sizePerPage + 1,
      after: filters.lastId,
      order_by: filters.sortConfig.key,
      order: filters.sortConfig.direction,
      ...(filters.search
        ? { 'search[columns]': 'event.type', 'search[term]': filters.search }
        : {}),
      ...(filters.after_created_on ? { after_created_on: filters.after_created_on } : {}),
      ...(filters.before_created_on
        ? { before_created_on: filters.before_created_on }
        : {}),
    },
    {
      enabled: false,
    }
  );

  useEffect(() => {
    refetch().then((res) => {
      if (res.status === 'success') {
        const { data } = res.data;
        const eventsData = data?.map((event: IHardwareEvent) => {
          const { type } = event;
          const colorType = EVENT_LOGS_TYPES.find(({ value }) => value === type);
          return {
            ...event,
            color_type: (colorType?.color as SeverityPillColor) ?? null,
          };
        });
        if (filters.lastId && eventsData) {
          setEvents((event) => [...event, ...eventsData]);
        } else {
          setEvents(eventsData);
        }
      }
    });

    // if isComplete is true, then check if after_created_on or before_created_on are set and add them to chips
    if (isComplete) {
      if (filters.after_created_on) {
        handleDateChange('startDate', new Date(filters.after_created_on));
      }
      if (filters.before_created_on) {
        handleDateChange('endDate', new Date(filters.before_created_on));
      }
    }
  }, [refetch, filters, isComplete]);

  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null): void => {
    if (date) {
      setChips((prevChips) => {
        const chip: SearchChip = {
          label: field === 'startDate' ? 'Start Date' : 'End Date',
          field: field,
          value: date,
          displayValue: date.toLocaleDateString(),
        };
        return [...prevChips.filter((chip) => chip.field !== field), chip];
      });
    }
  };

  const handleChipDelete = (deletedGroup: SearchChip) => {
    if (deletedGroup.label === 'Start Date') {
      setFilters((prev) => ({
        ...prev,
        after_created_on: '',
      }));
      setChips((prevChips) => prevChips.filter((chip) => chip.field !== 'startDate'));
      setSelectedAfterCreatedOn(null);
    }
    if (deletedGroup.label === 'End Date') {
      setFilters((prev) => ({
        ...prev,
        before_created_on: '',
      }));
      setChips((prevChips) => prevChips.filter((chip) => chip.field !== 'endDate'));
      setSelectedbeforeCreatedOn(null);
    }
  };

  return (
    <>
      <CardView
        headerActions={
          <IconButtonMenu
            buttons={[
              {
                label: 'Export',
                onClick: () => setExportData(true),
                startIcon: <FileDownloadIcon />,
              },
            ]}
          />
        }
        content={
          <>
            <PaginationTable
              dataId={'hardware-event-logs'}
              showFilter
              customKey={'id'}
              sortDirection={'desc'}
              exportData={exportData}
              csvFileName={'Hardware_Events_Logs.csv'}
              onExport={() => setExportData(false)}
              sortBy={filters.sortConfig.key}
              chips={chips}
              handleChipDelete={handleChipDelete}
              onApplyFilter={() => {
                if (selectedAfterCreatedOn) {
                  handleDateChange('startDate', new Date(selectedAfterCreatedOn));
                  setFilters((prev) => ({
                    ...prev,
                    after_created_on: new Date(selectedAfterCreatedOn).toISOString(),
                    lastId: null,
                  }));
                }
                if (selectedbeforeCreatedOn) {
                  handleDateChange('endDate', new Date(selectedbeforeCreatedOn));
                  setFilters((prev) => ({
                    ...prev,
                    before_created_on: selectedbeforeCreatedOn
                      ? selectedbeforeCreatedOn.toISOString()
                      : '',
                    lastId: null,
                  }));
                }
              }}
              spaceFilters={
                <>
                  <MovaticFieldGroupDate
                    id={'hardware-event-logs-start-date'}
                    label="Start Date"
                    value={new Date(filters.after_created_on || '') || null}
                    onChange={(date) => setSelectedAfterCreatedOn(date.toISOString())}
                  />
                  <MovaticFieldGroupDate
                    id={'hardware-event-logs-end-date'}
                    label="End Date"
                    value={new Date(filters.before_created_on || '') || null}
                    onChange={(date) => setSelectedbeforeCreatedOn(date.toISOString())}
                  />
                </>
              }
              onSortChange={(sort) =>
                setFilters({
                  ...filters,
                  sortConfig: {
                    key: sort.key,
                    direction: sort.direction as 'desc' | 'asc',
                  },
                })
              }
              handleSearch={(event) =>
                setFilters({ ...filters, search: event.target.value })
              }
              searchText={filters.search}
              isLoading={
                isLoading ||
                isRefetching ||
                !hardware_id ||
                isLoadingHardware ||
                !isComplete ||
                isRestoring
              }
              columns={eventLogsColumns as IColumn[]}
              items={events || []}
              onPageChange={(page, newPage) => {
                const pageNumberAux = newPage + 1;
                if (events?.length / filters.sizePerPage < pageNumberAux) {
                  setFilters({ ...filters, lastId: events[events.length - 1].id });
                }
                setFilters({ ...filters, pageNumber: newPage });
              }}
              onRowsPerPageChange={(pageSizeString) => {
                const pageSize = Number(pageSizeString.target.value);
                setFilters({ ...filters, sizePerPage: pageSize });
              }}
              page={filters.pageNumber}
              rowsPerPage={filters.sizePerPage}
              searchPlaceholder={'Search Event Logs'}
              noDataText={`No event logs for this ${type}`}
              localSearch={false}
            />
          </>
        }
        title={'Event Logs'}
      />
    </>
  );
};

export default EventLogs;
